import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { REFERRALS_TAG_TYPE } from './constants';
import type { ReferralsResponse } from './types';

interface GetReferralsV2Params {
  providerId: string;
  queryParams: string;
  data: Record<string, any>;
}

interface ExportReferralsParams {
  providerId: string;
  queryParams: string;
  data: Record<string, any>;
  fileFormat: string;
  columns: string[];
  fileName: string;
}

export const referralsApi = createApi({
  reducerPath: 'referrals',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [REFERRALS_TAG_TYPE],
  endpoints: (builder) => ({
    getReferralsV2: builder.query<ReferralsResponse, GetReferralsV2Params>({
      query: ({ providerId, queryParams, data }) => ({
        url: `v1/entities/v2/entity/${providerId}/referrals/${queryParams}`,
        method: 'POST',
        body: data,
      }),
      providesTags: (result, error, { providerId, queryParams }) => [
        { type: REFERRALS_TAG_TYPE, id: `${providerId}-${queryParams}` },
      ],
    }),
    getReferralsV2Paginated: builder.infiniteQuery<
      ReferralsResponse,
      GetReferralsV2Params,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (!lastPage.next) return undefined;
          return lastPageParam + 1;
        },
      },
      query({ queryArg, pageParam }) {
        const paginatedParams = queryArg.queryParams.replace(
          /page=\d+/,
          `page=${pageParam}`
        );
        return {
          url: `v1/entities/v2/entity/${queryArg.providerId}/referrals/${paginatedParams}`,
          method: 'POST',
          body: queryArg.data,
        };
      },
      providesTags: [REFERRALS_TAG_TYPE],
    }),
    exportReferrals: builder.mutation<void, ExportReferralsParams>({
      query: ({ providerId, queryParams, data, fileFormat, columns, fileName }) => {
        const url = `https://api.medscout.io/api/v1/entities/v2/entity/${providerId}/referrals/${queryParams}`;

        return {
          url: 'v1/admin/export/',
          method: 'POST',
          body: {
            method: 'POST',
            url,
            file_format: fileFormat,
            file_name: fileName,
            columns,
            body: data,
          },
        };
      },
    }),
  }),
});

export const {
  useGetReferralsV2Query,
  useGetReferralsV2PaginatedInfiniteQuery,
  useExportReferralsMutation,
} = referralsApi;
